import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, ShieldCheck, ShoppingCart, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Logo } from "@/components/bits";
import { DISCORD_URL } from "@/lib/images";

const links = [
  { to: "/", label: "HOME" },
  { to: "/store", label: "STORE" },
  { to: "/vouches", label: "VOUCHES" },
  { to: "/status", label: "STATUS" },
  { to: "/faq", label: "FAQ" },
  { to: "/terms", label: "TERMS" },
];

export default function Nav() {
  const { user, logout } = useAuth();
  const cart = useCart();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link"><Logo size={40} /></Link>

        <nav className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`font-mono text-[11px] font-medium tracking-[0.22em] transition-colors hover:text-white pb-1 border-b-2 ${loc.pathname === l.to ? "text-white border-white" : "text-white/55 border-transparent"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button onClick={() => cart.setOpen(true)} data-testid="nav-cart-btn" className="relative p-2 text-white/70 hover:text-white transition-colors" title="Cart">
            <ShoppingCart size={17} />
            {cart.count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 bg-white text-black font-mono text-[9px] w-4 h-4 rounded-full flex items-center justify-center">{cart.count}</span>
            )}
          </button>
          {user && user.role ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard-btn" className="flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] text-white/70 hover:text-white transition-colors px-2 py-2">
                {user.discord_avatar_url
                  ? <img src={user.discord_avatar_url} alt="" className="h-6 w-6 rounded-full border border-white/25 object-cover" data-testid="nav-discord-avatar" />
                  : <User size={14} />} {user.discord_global_name || user.discord_username || user.name || "ACCOUNT"}
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin-btn" className="p-2 text-white/70 hover:text-white transition-colors" title="Admin">
                  <ShieldCheck size={15} />
                </Link>
              )}
              <button onClick={() => { logout(); nav("/"); }} data-testid="nav-logout-btn" className="p-2 text-white/70 hover:text-white transition-colors">
                <LogOut size={14} />
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login-btn" className="font-mono text-[11px] tracking-[0.2em] text-white/70 hover:text-white transition-colors px-3 py-2">LOGIN</Link>
          )}
          <a href={DISCORD_URL} target="_blank" rel="noreferrer" data-testid="nav-discord-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/[0.04] backdrop-blur px-5 py-2.5 font-mono text-[11px] font-bold tracking-[0.15em] text-white hover:bg-white/10 hover:border-white/60 transition-all">
            <MessageCircle size={14} /> JOIN DISCORD
          </a>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-black border-t border-white/10 px-5 py-6 space-y-4" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block font-mono text-sm tracking-[0.2em] text-white/70">{l.label}</Link>
          ))}
          <a href={DISCORD_URL} target="_blank" rel="noreferrer" className="block font-mono text-sm tracking-[0.2em] text-white/70">DISCORD</a>
          <button onClick={() => { setOpen(false); cart.setOpen(true); }} data-testid="mobile-cart-btn" className="block font-mono text-sm tracking-[0.2em] text-white/70">
            CART {cart.count > 0 ? `(${cart.count})` : ""}
          </button>
          <div className="pt-3 flex gap-3">
            {user && user.role ? (
              <>
                <Link to="/dashboard" data-testid="mobile-dashboard-link" className="btn-ghost px-4 py-2.5 text-xs flex-1 text-center">ACCOUNT</Link>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="mobile-admin-link" className="btn-inj px-4 py-2.5 text-xs flex-1 text-center">ADMIN PANEL</Link>
                )}
                <button onClick={() => { logout(); nav("/"); }} data-testid="mobile-logout-btn" className="btn-ghost px-4 py-2.5 text-xs">LOGOUT</button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost px-4 py-2.5 text-xs flex-1 text-center">LOGIN</Link>
                <Link to="/store" className="btn-inj px-4 py-2.5 text-xs flex-1 text-center">SHOP</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
